import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Loader2, Crown } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Checkbox } from "@/components/ui/checkbox";

interface GrantPremiumDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  userId: string;
  userName: string;
  userEmail: string;
  onSuccess: () => void;
}

export function GrantPremiumDialog({
  open,
  onOpenChange,
  userId,
  userName,
  userEmail,
  onSuccess
}: GrantPremiumDialogProps) {
  const [loading, setLoading] = useState(false);
  const [premiumProducts, setPremiumProducts] = useState<Array<{ id: string; name: string }>>([]);
  const [selectedProducts, setSelectedProducts] = useState<string[]>([]);
  const [productsLoading, setProductsLoading] = useState(false);
  const { toast } = useToast();

  // Fetch premium products when dialog opens
  useState(() => {
    if (open) {
      setProductsLoading(true);
      supabase
        .from('premium_products')
        .select('product_id, product_name')
        .then(({ data, error }) => {
          if (error) {
            console.error('Error fetching premium products:', error);
            toast({
              variant: "destructive",
              title: "Error",
              description: "Failed to load premium products"
            });
          } else {
            setPremiumProducts(
              (data || []).map(p => ({ id: p.product_id, name: p.product_name }))
            );
            // Select all by default
            setSelectedProducts((data || []).map(p => p.product_id));
          }
          setProductsLoading(false);
        });
    }
  });

  const handleGrant = async () => {
    if (selectedProducts.length === 0) {
      toast({
        variant: "destructive",
        title: "No products selected",
        description: "Please select at least one product to grant"
      });
      return;
    }

    setLoading(true);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session?.access_token) {
        throw new Error('No access token');
      }

      const response = await fetch(
        'https://xuqkgeusasosxtcfhbwj.supabase.co/functions/v1/assign-user-products',
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            userId,
            productIds: selectedProducts,
            source: 'admin_grant'
          })
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();

      toast({
        title: "Premium access granted",
        description: `Successfully granted ${data.count} product(s) to ${userName}`
      });

      onSuccess();
      onOpenChange(false);
    } catch (error: any) {
      console.error('Error granting premium:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message || "Failed to grant premium access"
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Crown className="h-5 w-5 text-yellow-500" />
            Grant Premium Access
          </DialogTitle>
          <DialogDescription>
            Grant premium access to <strong>{userName}</strong> ({userEmail})
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label>Premium Products</Label>
            {productsLoading ? (
              <div className="flex items-center justify-center py-4">
                <Loader2 className="h-6 w-6 animate-spin" />
              </div>
            ) : (
              <div className="space-y-2">
                {premiumProducts.map(product => (
                  <div key={product.id} className="flex items-center space-x-2">
                    <Checkbox
                      id={product.id}
                      checked={selectedProducts.includes(product.id)}
                      onCheckedChange={(checked) => {
                        if (checked) {
                          setSelectedProducts([...selectedProducts, product.id]);
                        } else {
                          setSelectedProducts(selectedProducts.filter(id => id !== product.id));
                        }
                      }}
                    />
                    <label
                      htmlFor={product.id}
                      className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                    >
                      {product.name}
                    </label>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="rounded-lg bg-muted p-3 text-sm">
            <p className="text-muted-foreground">
              This will grant immediate premium access via admin override. The user will bypass trial enforcement.
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button onClick={handleGrant} disabled={loading || productsLoading}>
            {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Grant Access
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
